import { Component } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import {
  BizFrontendApiService,
  BizFrontendSettingService,
  IsDateService,
  IsEnvironmentService,
  IsWbsService,
  ActionTransactionSettings,
  IsActionTransactionService,
  IsAccountService,
} from '@interserve/angular';
import { IsbeAuthLocalComponent } from './components/is-be-auth/isbe-auth.component';
import { HeaderComponent } from './components/header/header.component';
import { ScreenMessageComponent } from './components/screen-message/screen-message.component';
import { AccountService } from './services/account.service';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    IsbeAuthLocalComponent,
    HeaderComponent,
    ScreenMessageComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  isLoggedIn = false;
  isLoading = false;
  code: string = 'inventory-report';
  readonly isMaintenanceMode = environment.maintenanceMode;

  constructor(
    private accountService: AccountService,
    private _BizFrontendApiService: BizFrontendApiService,
    private _bizFrontendSettingService: BizFrontendSettingService,
    private _isEnvironmentService: IsEnvironmentService,
    private _isActionTransactionService: IsActionTransactionService,
    private _isAccountService: IsAccountService,
  ) {
    if (this.isMaintenanceMode) {
      return;
    }
    this._bizFrontendSettingService.defaultBucketCode = this.code;
    this.initializeEnvironment();
    this.subscribeToAccountChanges();
  }

  /**
   * 環境設定を初期化する
   */
  private initializeEnvironment(): void {
    this._isEnvironmentService.setAppSettings({
      appCd: this.code,
      //      environmentKey: environment.production ? 'prod' : 'dev',
      environmentKey: environment.production ? 'proSche_prod' : 'dev', // indexCoreは一旦ProSche専用に立てたのでそちらを参照
    });
  }

  /**
   * アカウント変更を購読し、変更時に初期化処理を実行する
   */
  private subscribeToAccountChanges(): void {
    this.accountService.account$.subscribe((account) => {
      this.handleAccountChange(account);
    });
  }

  /**
   * アカウント変更時の処理を実行する
   */
  private async handleAccountChange(account: any): Promise<void> {
    // アカウントがnullの場合はログアウト
    if (account === null) {
      this.accountService.logout();
      return;
    }

    // アカウントIDが存在しない場合は処理を終了
    if (!account?.accountId) {
      return;
    }

    await this.setupAccount(account);
    this.initializeApplication(account);
  }

  /**
   * アカウント情報を設定する
   */
  private async setupAccount(account: any): Promise<void> {
    // アカウントサービスにアカウント情報を設定
    this._isAccountService.account = account;
    this._isAccountService.organizations = account?.organizations ?? [];
    this._isAccountService.selectedOrganizationId =
      account.organizations?.[0]?.organizationId ?? '';
  }

  /**
   * ユーザーIDリストを作成する（アカウントID + 組織ID）
   */
  private createUserIds(account: any): string[] {
    const userIds: string[] = [account.accountId];
    const organizationIds: string[] = account.organizations.map(
      (org: any) => org.organizationId,
    );
    userIds.push(...organizationIds);
    return userIds;
  }

  /**
   * アプリケーションを初期化し、設定を読み込む
   */
  private initializeApplication(account: any): void {
    const userIds = this.createUserIds(account);

    this._BizFrontendApiService
      .initializeApp(environment.appCode, undefined, userIds)
      .subscribe((result) => {
        if (result) {
          this.handleInitializeResult();
        }
      });
  }

  /**
   * 初期化成功後のトランザクション設定を取得・設定する
   */
  private async handleInitializeResult(): Promise<void> {
    const ret = await this.getActionTransactionConfig();

    if (!ret?.data) {
      return;
    }

    const config = ret.data as ActionTransactionSettings;
    if (config) {
      this.setActionTransactionConfig(config);
      this.isLoggedIn = true;
    }
  }

  /**
   * ActionTransactionConfigを取得する
   * @returns 設定情報を含むPromise
   */
  private async getActionTransactionConfig(): Promise<any> {
    // frontEndCdはinventory-report_actionTransaction-config
    const ret = this._BizFrontendApiService.getShearedUserSettingsForSettingCd(
      'actionTransactionSettings',
    );

    return lastValueFrom(ret);
  }

  /**
   * ActionTransactionConfigを設定する
   * @param config 設定情報
   */
  private setActionTransactionConfig(config: ActionTransactionSettings): void {
    this._isActionTransactionService.setSettings(config);
  }
}
